import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

const SHORTCUTS = [
  ["Enter", "Begin / continue / dismiss"],
  ["F", "Toggle full screen"],
  ["Esc", "Reset to standby (new run)"],
  ["R", "Replay the last memory"],
  ["`", "Operator menu"],
  ["Any tag", "Wakes the kiosk from the briefing"],
];

export const OperatorPanel = ({ open, onClose, game, onReset }) => {
  const [settings, setSettings] = useState(null);
  const [authed, setAuthed] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const kioskUrl = window.location.origin + "/";

  useEffect(() => {
    if (!open) return;
    api.get("/admin/settings").then((r) => { setSettings(r.data); setAuthed(true); }).catch(() => setAuthed(false));
  }, [open]);

  if (!open) return null;

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/login", { passcode: code });
      const { data } = await api.get("/admin/settings");
      setSettings(data); setAuthed(true); setCode("");
    } catch (err) { toast.error(errMsg(err, "Access denied")); } finally { setBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", { ...settings, idle_minutes: Number(settings.idle_minutes), target_count: Number(settings.target_count) });
      toast.success("Applied & saved");
    } catch (err) { toast.error(errMsg(err, "Could not save")); } finally { setBusy(false); }
  };

  return (
    <aside data-testid="operator-panel" className="operator-drawer fixed right-4 top-4 bottom-4 z-[70] w-[min(94vw,460px)] overflow-y-auto border border-emerald-400/50 bg-[#050807]/97 p-6 shadow-[0_0_60px_rgba(0,255,157,0.12)] backdrop-blur-xl">
      <div className="flex items-center justify-between">
        <h2 className="brand text-2xl">Operator</h2>
        <button data-testid="operator-close" onClick={onClose} className="lab-btn-ghost py-1.5 text-sm">Close (`)</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link data-testid="operator-setup-link" to="/setup" className="lab-btn-ghost py-1.5 text-sm">/setup · programming bench</Link>
        <button data-testid="operator-reset" onClick={() => { onReset(); onClose(); }} className="lab-btn-ghost py-1.5 text-sm">Reset to standby</button>
      </div>
      <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-slate-500">
        Run: {game.decoded.length}/{game.total} memories · {game.sealed ? "sealed" : "calibration"}
      </p>

      {authed === false && (
        <form data-testid="operator-login" onSubmit={login} className="mt-6 border border-amber-300/40 p-4">
          <p className="scientist-label">Technician passcode to edit timings</p>
          <div className="mt-3 flex gap-2">
            <input data-testid="operator-passcode" type="password" value={code} onChange={(e) => setCode(e.target.value)} className="lab-input flex-1 tracking-[0.3em]" />
            <button data-testid="operator-login-submit" disabled={busy || !code} className="lab-btn-primary py-1.5">Unlock</button>
          </div>
        </form>
      )}
      {authed && settings && (
        <form data-testid="operator-settings" onSubmit={save} className="mt-6 grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="lab-label">Idle reset (min)</span>
              <input data-testid="operator-idle-input" type="number" min="1" max="60" value={settings.idle_minutes} onChange={(e) => setSettings({ ...settings, idle_minutes: e.target.value })} className="lab-input mt-1 w-full font-mono" />
            </label>
            <label className="block">
              <span className="lab-label">Target vocabulary</span>
              <input data-testid="operator-target-input" type="number" min="1" max="500" value={settings.target_count} onChange={(e) => setSettings({ ...settings, target_count: e.target.value })} className="lab-input mt-1 w-full font-mono" />
            </label>
          </div>
          <button data-testid="operator-save" disabled={busy} className="lab-btn-ghost justify-center">Apply &amp; save</button>
        </form>
      )}

      <div className="mt-8">
        <p className="lab-label">The Spore Project · kiosk help · Mac terminal</p>
        <code data-testid="operator-kiosk-cmd" className="mt-2 block border border-emerald-400/30 bg-slate-900 p-3 font-mono text-xs text-emerald-200 break-all">
          open -a "Google Chrome" --args --kiosk {kioskUrl}
        </code>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          Quit Chrome fully first (⌘Q). Kiosk mode has no browser UI; use ⌘Q to leave.
        </p>
        <p data-testid="operator-reader-help" className="mt-3 text-xs text-slate-400">
          Reader beeps but nothing happens here? Open <Link to="/setup" className="text-emerald-300 underline-offset-4 hover:underline">/setup → Reader check</Link>:
          it diagnoses the Mac side (Accessibility permission / "Typing active") and offers the reader bridge, which needs neither.
        </p>
      </div>

      <table className="mt-6 w-full font-mono text-sm">
        <tbody>
          {SHORTCUTS.map(([k, d]) => (
            <tr key={k} className="border-t border-emerald-400/15">
              <td className="py-2 pr-4 font-bold text-emerald-300">{k}</td>
              <td className="py-2 text-slate-300">{d}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </aside>
  );
};
